import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core'
import { TranslateModule, TranslateService } from '@ngx-translate/core'
import { ColumnConfig, ColumnFormat, TableComponent } from '@components/table/table.component'
import { FormsModule } from '@angular/forms'
import { MatFormField, MatPrefix, MatSuffix } from '@angular/material/form-field'
import {
    MatDatepickerToggle,
    MatDatepickerToggleIcon,
    MatDateRangeInput,
    MatDateRangePicker,
    MatEndDate,
    MatStartDate
} from '@angular/material/datepicker'
import { MatIcon } from '@angular/material/icon'
import { NgIf } from '@angular/common'
import { SelectComponent } from '@components/select/select.component'
import { PaginationComponent } from '@components/pagination/pagination.component'
import { SelectValue } from '../../interfaces/select-value.interface'
import { ApiService, UserInfo } from '../../services/api.service'
import { DateAdapter } from '@angular/material/core'
import { InvoiceData, PagedInvoiceList } from '../../interfaces/invoice-claim.interface'
import { AlertComponent } from '@components/alert/alert.component'

@Component({
  selector: 'app-invoice-selection',
  templateUrl: './invoice-selection.component.html',
  styleUrl: './invoice-selection.component.scss',
    standalone: true,
    imports: [
        TranslateModule,
        TableComponent,
        FormsModule,
        MatFormField,
        MatDateRangeInput,
        MatStartDate,
        MatEndDate,
        MatDatepickerToggle,
        MatIcon,
        MatDateRangePicker,
        MatPrefix,
        MatSuffix,
        MatDatepickerToggleIcon,
        NgIf,
        SelectComponent,
        PaginationComponent,
        AlertComponent
    ]
})
export class InvoiceSelectionComponent implements OnInit, OnChanges{

    @Input() userInfo: UserInfo;
    @Input() initialSelectedInvoices: InvoiceData[];
    @Input() type: 'claim' | 'sale';
    @Input() entityId?: string;
    @Input() disabledReason: string;
    @Input() relatedFabricators: SelectValue[] = []
    selectedRelatedFabricator: SelectValue | null = this.relatedFabricators.length > 1 ? this.relatedFabricators[0] : null
    @Input() tableColumns: ColumnConfig[] = [
        { key: 'invoiceId', header: 'create-claim.material-selection.table.invoice' },
        { key: 'position', header: 'create-claim.material-selection.table.position' },
        { key: 'lotId', header: 'create-claim.material-selection.table.lote' },
        { key: 'createdDate', header: 'create-claim.material-selection.table.date', format: 'date' as ColumnFormat },
        { key: 'translatedDescription', header: 'global.description', format: 'translated_material' as ColumnFormat },
        { key: 'materialId', header: 'create-claim.material-selection.table.material' },
        { key: 'availableUnits', header: 'create-claim.material-selection.table.available_m2_sqft', format: 'number' as ColumnFormat, formatOptions: { decimals: '1.2-2' } },
        { key: 'totalUnits', header: 'create-claim.material-selection.table.total_m2_sqft', format: 'number' as ColumnFormat, formatOptions: { decimals: '1.2-2' } },
        { key: 'measure', header: 'create-claim.material-selection.table.measure' },
        { key: 'cost', header: 'create-claim.material-selection.table.cost_net', format: 'currency' as ColumnFormat, formatOptions: { symbol: 'symbol', digits: '1.2-2' } },
    ];

    @Output() invoiceSelected: EventEmitter<InvoiceData[]> = new EventEmitter()

    maxDate = new Date()

    // Invoices filters
    startDate?: Date;
    endDate?: Date;
    invoiceNumber?: string;
    lotNumber?: string;
    color: SelectValue | null;

    availableColors: SelectValue[] = []

    // Table data
    filteredInvoices: InvoiceData[];
    currentPage: number = 0;
    totalPages: number = 1;
    pageSize: number = 9;
    totalInvoiceNumber: number = 0;
    selectedInvoices: any = [];

    constructor(
        private api: ApiService,
        private translate: TranslateService,
        private dateAdapter: DateAdapter<any>
    ) {
        this.translate.get([
            'create-claim.material-selection.table.invoice',
            'create-claim.material-selection.table.position',
            'create-claim.material-selection.table.lote',
            'create-claim.material-selection.table.date',
            'global.description',
            'create-claim.material-selection.table.material',
            'create-claim.material-selection.table.available_m2_sqft',
            'create-claim.material-selection.table.total_m2_sqft',
            'create-claim.material-selection.table.measure',
            'create-claim.material-selection.table.cost_net',
        ])
    }


    ngOnInit() {
        this.translate.onLangChange.subscribe((value) => {
            this.updateMatLang(value.lang)
        })
        this.updateMatLang(this.translate.currentLang || undefined)

        switch (this.type){
            case 'claim':
                this.disabledReason = this.translate.instant('create-claim.documentation.invoice_not_selectable')
                break;
            case 'sale':
                this.disabledReason = this.translate.instant('my_sales.invoice_not_selectable')
                break;
        }
    }

    ngOnChanges(changes: SimpleChanges) {
        if(changes['relatedFabricators']){
            this.selectedRelatedFabricator = this.relatedFabricators?.length > 1 ? this.relatedFabricators[0] : null
        }
    }

    private updateMatLang(lang?: string){
        if(lang){
            this.dateAdapter.setLocale(lang)
            return
        }

        if(this.userInfo.locale){
            this.dateAdapter.setLocale(this.userInfo.locale.slice(0, 2));
        } else {
            this.dateAdapter.setLocale('en');
        }
    }

    fetchInvoices(updateColorList = true) {
        if((this.startDate && this.endDate) || this.invoiceNumber || this.lotNumber || this.color) {
            if(updateColorList) this.fetchAvailableColors()
            switch (this.type){
                case 'claim':
                    this.getClaimInvoices()
                    break;
                case 'sale':
                    this.getSaleInvoices()
                    break;
            }

        }
    }

    private getClaimInvoices(){
        this.api.getClaimInvoices(
            this.currentPage,
            this.pageSize,
            this.startDate ? this.startDate.toISOString().split('T')[0] : undefined,
            this.endDate ? this.endDate.toISOString().split('T')[0] : undefined,
            this.selectedRelatedFabricator ? this.selectedRelatedFabricator.value : null,
            this.invoiceNumber,
            this.lotNumber,
            this.color ? this.color.value : null,
            this.entityId
        ).subscribe((response) => {
            this.parseInvoiceList(response)
        })
    }

    private getSaleInvoices(){
        this.api.getSaleInvoices(
            this.currentPage,
            this.pageSize,
            this.startDate ? this.startDate.toISOString().split('T')[0] : undefined,
            this.endDate ? this.endDate.toISOString().split('T')[0] : undefined,
            this.selectedRelatedFabricator ? this.selectedRelatedFabricator.value : null,
            this.invoiceNumber,
            this.lotNumber,
            this.color ? this.color.value : null,
            this.entityId
        ).subscribe((response) => {
            this.parseInvoiceList(response)
        })
    }

    private parseInvoiceList(response: PagedInvoiceList){
        this.totalPages = response.page.totalPages;
        this.totalInvoiceNumber = response.page.totalElements;
        response.content.forEach(invoice => {
            invoice.materialId = invoice.materialId?.replace(/^0+/, '') || invoice.materialId
            invoice.id = invoice.invoiceId + '-' + invoice.position
            if(invoice.isSelected) {
                this.selectedInvoices.push(invoice)
            }
        })
        this.filteredInvoices = response.content
    }

    private fetchAvailableColors() {
        const startDateStr = this.startDate ? this.startDate.toISOString().split('T')[0] : undefined
        const endDateStr = this.endDate ? this.endDate.toISOString().split('T')[0] : undefined

        this.api.getAvailableInvoiceColors(
            startDateStr,
            endDateStr,
            this.selectedRelatedFabricator ? this.selectedRelatedFabricator.value : null,
            this.invoiceNumber,
            this.lotNumber,
            this.color ? this.color.value : undefined
        ).subscribe((colors) => {
            this.availableColors = colors.map(c => ({
                id: c.id,
                label: c.label,
                value: c.value
            }))
        })
    }

    onColorSelect($event: InvoiceData[]) {
        this.invoiceSelected.emit($event)
    }

    onStartDateChange($event: any) {
        this.startDate = $event.value
    }

    onEndDateChange($event: any) {
        this.endDate = $event.value
        if(this.endDate){
            this.fetchInvoices()
        }
    }

    clearDateRange() {
        this.startDate = undefined;
        this.endDate = undefined;
    }

    onColorFilter($event: SelectValue | null) {
        this.color = $event
        this.fetchInvoices(!this.color)
    }

    onPageChange(page: number) {
        this.currentPage = page;
        this.fetchInvoices()
    }

    onFilter() {
        this.currentPage = 0
        this.totalInvoiceNumber = 0
        this.totalPages = 1
        this.fetchInvoices()
    }

    onFabricatorSelected(fabricator: SelectValue | null) {
        this.selectedRelatedFabricator = fabricator
        this.invoiceNumber = undefined
        this.lotNumber = undefined
        this.color = null
        if(this.startDate && this.endDate){
            this.fetchInvoices()
        }
    }
}
